import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    Phone,
    Video,
    MessageSquareText,
    Layers,
} from "lucide-react";
import { useSelector } from "react-redux";
import axios from "axios";
import { ICON_MAP } from "../../utilities/iconcatalog.tsx";

const QuickIcon = ({ name, className = "w-5 h-5" }) => {
    if (name === "chat") return <MessageSquareText className={className} />;
    if (name === "call") return <Phone className={className} />;
    return <Video className={className} />;
};

export function TeletorgetHero({
    ctas = [
        { label: "Book konsultasjon", href: "#agents", kind: "primary" },
        { label: "Registrer deg", href: "#signup", kind: "secondary" },
    ],
    onRegisterClick,
    baseUrl,
    handleChipClick,
    from,
    setHeroData,
    heroData,
}) {
    const client = useSelector((state) => state.client.client);
    const default_configs = client ? JSON.parse(client.default_configs) : {};
    const footer_settings = client ? JSON.parse(client.footer_settings) : {};

    const hero_settings = client ? JSON.parse(client.hero_settings) : {};
    const theme_settings = client ? JSON.parse(client.theme_settings) : {};

    const [title, setTitle] = useState("Title Here");
    const [body, setBody] = useState("Body Here");

    const DEFAULT_HERO_IMAGES = [
        "../../hero.png",
        "../../hero-court.jpg",
        "../../hero-global.png",
    ];
    const [images, setImages] = useState(DEFAULT_HERO_IMAGES);
    const heroImages =
        hero_settings?.selectedHero?.[theme_settings?.selectedHeroKey]?.images;

    useEffect(() => {
        const nextImages =
            Array.isArray(heroImages) && heroImages.length
                ? heroImages
                : DEFAULT_HERO_IMAGES;

        setImages(prev =>
            JSON.stringify(prev) === JSON.stringify(nextImages)
                ? prev
                : nextImages
        );
    }, [heroImages]);

    const quick = [
        { label: "Chat", href: "#chat", icon: "chat" },
        { label: "Ring", href: "#", icon: "call" },
        { label: "Video", href: "#agents", icon: "video" },
    ];

    const [chips, setChips] = useState([]);
    const [currentSlide, setCurrentSlide] = useState(0);

    /* Fetch chips */
    useEffect(() => {
        if (!client?.uid) return;

        axios
            .get(baseUrl + "/getSkills", { params: { client_uid: client.uid } })
            .then((response) => {
                if (response.data.status === "OK") {
                    setChips(response.data.data);
                }
            });
    }, [client]);

    /* Auto-slide */
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % images.length);
        }, 10000);
        return () => clearInterval(interval);
    }, [images.length]);

    useEffect(() => {
        if (currentSlide >= images.length) {
            setCurrentSlide(0);
        }
    }, [images, currentSlide]);

    const onRingClick = () => {
        if (default_configs.WebRTC !== "yes") {
            window.location.href = "tel:" + footer_settings.footer_phone;
        } else {
            document.getElementById("agents")?.scrollIntoView({ behavior: "smooth" });
        }
    };

    const [titleInput, setTitleInput] = useState(title || null);
    const [titleChange, setTitleChange] = useState(false);
    const [bodyInput, setBodyInput] = useState(body || null);
    const [bodyChange, setBodyChange] = useState(false);
    const [imageFiles, setImageFiles] = useState([]);
    const handleTitleClick = (e) => {
        e.preventDefault();
        if (from === "support-client") {
            setTitleChange(true);
        }
    }

    const handleBodyClick = (e) => {
        e.preventDefault();
        if (from === "support-client") {
            setBodyChange(true);
        }
    }


    useEffect(() => {
        function handleKeyDown(event) {
            if (event.key === "Escape") {
                if (titleChange) setTitleChange(false);
                if (bodyChange) setBodyChange(false);
            }

            if (event.key === "Enter") {
                if (titleChange) {
                    setTitle(titleInput);
                    setTitleChange(false);
                }

                if (bodyChange) {
                    setBody(bodyInput);
                    setBodyChange(false);
                }
            }
        }

        document.addEventListener("keydown", handleKeyDown);

        setHeroData({
            title: titleInput,
            body: bodyInput,
            images: imageFiles,
            primaryColor: heroData?.primaryColor ? heroData.primaryColor : null,
            secondaryColor: heroData?.secondaryColor ? heroData.secondaryColor : null,
            wallet: heroData?.wallet ? heroData.wallet : null,
            events: heroData?.events ? heroData.events : null,
        })
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [titleChange, bodyChange, titleInput, bodyInput, images]);

    const handleImageSelect = (e) => {
        const files = Array.from(e.target.files);

        if (!files.length) return;

        const newImages = files.map((file) =>
            URL.createObjectURL(file)
        );

        // setImages((prev) => [...prev, ...newImages]); to append pictures
        // to set new added images
        setImages(newImages);
        setImageFiles((prev) => [...prev, ...files]);
        setCurrentSlide(0);
    };

    useEffect(() => {
        return () => {
            images.forEach((img) => URL.revokeObjectURL(img));
        };
    }, []);

    return (
        <div className="w-full max-w-full mx-auto">
            <div className="relative w-full overflow-hidden shadow-xl h-screen">

                {/* === BACKGROUND CAROUSEL === */}
                <AnimatePresence>
                    <motion.img
                        key={currentSlide}
                        src={images[currentSlide]}
                        alt="Hero background"
                        initial={{ opacity: 0, scale: 1, filter: "blur(6px)" }}
                        animate={{ opacity: 1, scale: 1.12, filter: "blur(0px)" }}
                        exit={{
                            opacity: 0,
                            transition: { duration: 0.6 }
                        }}
                        transition={{
                            opacity: { duration: 0.8, ease: "easeOut" },
                            scale: {
                                duration: 10, // 👈 slow cinematic zoom
                                ease: "easeOut",
                            },
                        }}
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                </AnimatePresence>

                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />

                {/* === CONTENT === */}
                <div className="relative z-10 flex items-center justify-start h-full py-8 md:py-0">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-white max-w-lg sm:max-w-xl md:max-w-2xl px-4 sm:px-6 md:px-12"
                    >
                        {from === "support-client" && (
                            <input type="file" multiple="multiple" accept="image/png, image/jpeg" onChange={handleImageSelect} className="w-full" />
                        )}
                        {!titleChange && (
                            <h1 onClick={(e) => handleTitleClick(e)} className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
                                {title}
                            </h1>
                        )}
                        {titleChange && (
                            <input type="text" onChange={(e) => setTitleInput(e.target.value)} className="max-w-lg sm:max-w-xl md:max-w-2xl text-black w-full" />
                        )}

                        {!bodyChange && (
                            <p onClick={(e) => handleBodyClick(e)} className="mt-4 text-base sm:text-lg md:text-xl text-white/90">
                                {body}
                            </p>
                        )}
                        {bodyChange && (
                            <textarea onChange={(e) => setBodyInput(e.target.value)} className="mt-4 text-base sm:text-lg md:text-xl text-black w-full"></textarea>
                        )}


                        {/* Chips */}
                        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">

                            {from === "support-client" && [1, 2, 3].map((c, i) => {
                                let HeaderIcon = null;


                                const iconDef = c.picture ? ICON_MAP[c.picture] : undefined;
                                HeaderIcon = iconDef?.Icon || Layers;
                                return (
                                    <button
                                        key={c.id ?? i}
                                        onClick={() => handleChipClick(c)}
                                        className="flex items-center gap-2 rounded-xl bg-white/95 text-gray-900 px-4 py-3 text-sm hover:bg-white transition"
                                    >
                                        {/* Image option */}

                                        {HeaderIcon && (
                                            <HeaderIcon className="h-4 w-4 text-sky-600" />
                                        )}

                                        <span className="font-medium">Skill {c}</span>
                                    </button>
                                )
                            })}

                            {from === "home" && chips.map((c, i) => {
                                let HeaderIcon = null;


                                const iconDef = c.picture ? ICON_MAP[c.picture] : undefined;
                                HeaderIcon = iconDef?.Icon || Layers;

                                return (
                                    <button
                                        key={c.id ?? i}
                                        onClick={() => handleChipClick(c.name)}
                                        className="flex items-center gap-2 rounded-xl bg-white/95 text-gray-900 px-4 py-3 text-sm hover:bg-white transition"
                                    >
                                        {/* Image option */}

                                        {HeaderIcon && (
                                            <HeaderIcon className="h-4 w-4 text-sky-600" />
                                        )}

                                        <span className="font-medium">{c.name}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* CTAs */}
                        <div className="mt-6 flex flex-wrap gap-3">
                            {ctas.map((c, i) => (
                                <a
                                    key={i}
                                    href={c.href}
                                    onClick={(e) => {
                                        if (c.kind === "secondary") {
                                            e.preventDefault();
                                            onRegisterClick?.();
                                        }
                                    }}
                                    style={{
                                        backgroundColor:
                                            from === "support-client"
                                                ? c.kind === "primary"
                                                    ? heroData.primaryColor
                                                    : heroData.secondaryColor
                                                : undefined,
                                    }}
                                    className={`px-5 py-3 rounded-xl font-semibold text-sm transition
                                        ${from === "support-client"
                                            ? c.kind === "primary"
                                                ? "text-white"
                                                : "text-gray-900"
                                            : c.kind === "primary"
                                                ? "bg-[var(--primary-color)] text-white hover:bg-[var(--primary-color)]/10"
                                                : "bg-[var(--secondary-color)] text-gray-900 hover:bg-[var(--primary-color)]/10"
                                        }
                                    `}
                                >
                                    {c.label}
                                </a>
                            ))}
                        </div>

                        {/* Quick actions */}
                        <div className="mt-5 flex flex-wrap gap-3">
                            {quick.map((q, i) => (
                                <a
                                    key={i}
                                    href={q.href}
                                    onClick={(e) => {
                                        if (q.icon === "call") {
                                            e.preventDefault();
                                            onRingClick();
                                        }
                                    }}
                                    className="inline-flex items-center gap-2 rounded-full bg-white/95 text-gray-900 px-4 py-2 text-sm hover:bg-white transition"
                                >
                                    <QuickIcon name={q.icon} />
                                    {q.label}
                                </a>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* === DOTS === */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                    {images.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setCurrentSlide(i)}
                            className={`w-2.5 h-2.5 rounded-full transition ${i === currentSlide ? "bg-white" : "bg-white/40"
                                }`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}


export function Hero({ hero, onRegisterClick, baseUrl, handleChipClick }) {
    return (
        <div className="min-h-screen bg-neutral-100">
            <TeletorgetHero onRegisterClick={onRegisterClick} baseUrl={baseUrl} handleChipClick={handleChipClick} from={"home"} setHeroData={() => console.log('e')} heroData={null} />
        </div>
    );
}